import type { Category } from '@cobliteam/api-lib/src/__generated__/graphql';
import { useTCO } from '@cobliteam/hooks';
import { useTranslation } from '@cobliteam/i18n';
import Button from '@cobliteam/ui/components/Button';
import Dropdown from '@cobliteam/ui/components/Dropdown';
import Checkbox from '@cobliteam/ui/form/Checkbox';
import InputSearch from '@cobliteam/ui/form/InputSearch';
import Label from '@cobliteam/ui/form/Label';
import Icon from '@cobliteam/ui/icons';
import { useCallback, useEffect, useState } from 'react';

export interface MultiSelectExpenseCategoryProps {
  isOpen?: boolean;
  size?: 'sm' | 'md';
  selectedOptions?: string[];
  onChange: (selectedOptions: string[]) => void;
}

const MultiSelectExpenseCategory = ({ isOpen, size = 'md', selectedOptions = [], onChange }: MultiSelectExpenseCategoryProps) => {
  const { t } = useTranslation('multiselects');
  const [forceClosed, setForceClosed] = useState(false);
  const [draft, setDraft] = useState(selectedOptions || []);
  const [search, setSearch] = useState<string | undefined>(undefined);

  const { data: categories = [], isLoading } = useTCO.useGetCategories();

  const hasSameElements = selectedOptions?.length === draft?.length && selectedOptions.every((value) => draft.find((d) => d === value));

  const onClick = useCallback(() => {
    setDraft(selectedOptions || []);
    setSearch(undefined);
  }, [selectedOptions]);

  const onChangeSelect = (categoryId: string) => {
    const isSelected = draft.find((d) => d === categoryId);
    const updatedSelected = isSelected ? draft.filter((d) => d !== categoryId) : [...draft, categoryId];
    setDraft(updatedSelected);
  };

  const handleClose = useCallback(() => {
    setForceClosed(true);
    setDraft([]);
    setSearch(undefined);
  }, []);

  const handleApply = useCallback(() => {
    onChange(draft);
    handleClose();
  }, [draft, handleClose, onChange]);

  const filterBySearch = useCallback((category: Category) => (search ? category.name.toLowerCase().includes(search.toLowerCase()) : true), [search]);

  const filteredCategories = categories.filter(filterBySearch).sort((a, b) => a.name.localeCompare(b.name));

  useEffect(() => setForceClosed(!isOpen), [isOpen]);

  useEffect(() => {
    if (forceClosed) setForceClosed(false);
  }, [forceClosed]);

  return (
    <Dropdown
      onOpenChange={onClick}
      forceOpen={isOpen}
      forceClose={forceClosed}
      portal={false}
      overlay={
        <Dropdown.Overlay>
          <Dropdown.Header className='px-2'>
            <Button variant='ghost' size='sm' disabled={draft?.length === 0} onClick={() => setDraft([])}>
              {t('expense_category.clear_selection')}
            </Button>
          </Dropdown.Header>
          <Dropdown.Search className='px-2'>
            <InputSearch placeholder={t('expense_category.search_placeholder')} value={search} autoFocus debounceInMillis={500} onChange={setSearch} className='w-full' />
          </Dropdown.Search>
          <Dropdown.Body className='px-2'>
            <Dropdown.List
              heightList={300}
              heightItem={28}
              data={filteredCategories}
              isLoading={isLoading}
              loadingComponent={<Dropdown.Loading />}
              itemComponent={(item: Category) => (
                <div className='flex items-center gap-2'>
                  <Checkbox id={item.id} key={item.id} checked={draft.includes(item.id)} onCheckedChange={() => onChangeSelect(item.id)} />
                  <Label htmlFor={item.id}>{item.name}</Label>
                </div>
              )}
            />
          </Dropdown.Body>
          <Dropdown.Footer>
            <Dropdown.Actions className='px-2'>
              <Button full variant='ghost' size='sm' onClick={handleClose}>
                {t('expense_category.cancel')}
              </Button>
              <Button full variant='contained' size='sm' color='primary' onClick={handleApply} disabled={hasSameElements}>
                {t('expense_category.apply')}
              </Button>
            </Dropdown.Actions>
          </Dropdown.Footer>
        </Dropdown.Overlay>
      }
    >
      <Button size={size} variant='ghost' startIcon={<Icon name='wallet-cards' />}>
        {t('expense_category.title')} {(selectedOptions?.length || 0) > 0 ? `(${selectedOptions?.length})` : ''}
      </Button>
    </Dropdown>
  );
};

export default MultiSelectExpenseCategory;
